// imports
import { FC, useCallback, useMemo, useState } from 'react';
// components
import InfiniteSelect from './InfiniteSelect';
// interfaces, constants, helper
import { HTTP_STATUS } from 'constants/index';
import { renderSandComponent } from 'lib/helper';
import { SandComponentSelectProps } from 'interfaces';
import { SandComponentsPayload, useFindAllSandComponentForSelectorLazyQuery } from 'generated/graphql';

const SandComponentSelect: FC<SandComponentSelectProps> = ({
  name,
  title,
  value,
  isActive,
  handleChange,
  margin = 'dense',
  isClearable = false,
  ...rest
}) => {
  const [options, setOptions] = useState<SandComponentsPayload['data']>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [getLoading, setGetLoading] = useState<boolean>(false);

  const updatedOptions = useMemo(() => {
    if (options) {
      return renderSandComponent(options);
    }
    return [];
  }, [options]);

  const [fetchSelectorData, { loading }] = useFindAllSandComponentForSelectorLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { findAllSandComponent } = data;
      const { data: sandComponent, response, pagination } = findAllSandComponent || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        const { totalPages: pages } = pagination || {};
        setTotalPages(pages);
        setOptions([
          ...(getLoading ? [] : options || []),
          ...((sandComponent as SandComponentsPayload['data']) ?? []),
        ]);
        setGetLoading(false);
      } else {
        setGetLoading(false);
      }
    },
    onError: () => {
      setOptions([]);
      setGetLoading(false);
    },
  });

  const fetchData = useCallback(
    async (page?: number, query?: string) => {
      if (!page) setGetLoading(true);
      await fetchSelectorData({
        variables: {
          findAllSandComponentInput: {
            ...(query && { search: query }),
            paginationOptions: { page: page || 1, limit: 10 },
            ...(typeof isActive === 'boolean' && { isActive }),
          },
        },
      });
    },
    [fetchSelectorData, isActive],
  );

  return (
    <InfiniteSelect
      {...rest}
      name={name}
      title={title}
      value={value}
      margin={margin}
      loading={loading}
      fetchData={fetchData}
      setOptions={setOptions}
      totalPages={totalPages}
      getLoading={getLoading}
      options={updatedOptions}
      isClearable={isClearable}
      handleChange={handleChange}
    />
  );
};

export default SandComponentSelect;
